import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, FormField, JalaliDatePicker, SegmentedControl, Select, Spinner, TaskOverlay, TextInput,
} from '@parsina/framework';
import { CT } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useDirtyClose } from '../../../components/common/DirtyCloseGuard';
import { userErrorMessage } from '../../../lib/userErrorMessage';
import {
  DAY_OFF_FIELDS, DAY_OFF_FIELD_OF_ERROR, DAY_OFF_REASON_MAX, dayOffVariables, emptyDayOffForm,
  isDayOffFormDirty, timeOptions, validateDayOff,
} from '../onlineBooking.shared';

// ─────────────────────────────────────────────────────────────────────────────
// «افزودن روز مرخصی» — one short task (≤ 6 fields) in TaskOverlay, a Dialog on a
// workstation (DESIGN_CONCEPT §5.2).
//
//   از تاریخ · تا تاریخ (اختیاری — empty is that one day) · مدت: تمام روز |
//   بخشی از روز (از ساعت · تا ساعت, on every day of the range) · علت (اختیاری)
//
// The kit's JalaliDatePicker renders no label and no error of its own, so each
// one is wrapped in FormField (it forwards `id`/`data-testid` to its input). Its
// value is the Gregorian "YYYY-MM-DD" the BFF wants, shown in the reader's calendar.
//
// Validation is the shared rules (onlineBooking.shared.js) on submit, focusing the
// first invalid field; a refusal the BFF names lands on its field, anything else
// in an inline Alert — the form keeps every keystroke and the button comes back
// (§2.4). Nothing is optimistic: the dialog closes on the server's answer, and
// the new row appearing in the list is the confirmation.
// ─────────────────────────────────────────────────────────────────────────────

export default function DayOffDialog({
  open, onClose, clinicId, clinicName, today, onSubmit, strings, testid = 'settings-booking-dayoff-dialog',
}) {
  const { t } = strings;
  const { formatDigits } = useTranslation();
  const [form, setForm] = useState(emptyDayOffForm);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);
  const formId = `${testid}-form`;
  const fieldId = (name) => `${testid}-${name}`;

  // A fresh form on every open: the last one's dates are not this one's.
  useEffect(() => {
    if (!open) return;
    setForm(emptyDayOffForm());
    setErrors({});
    setFormError('');
    setBusy(false);
  }, [open]);

  const dirty = isDayOffFormDirty(form);
  const { requestClose, guard } = useDirtyClose(dirty && !busy, onClose);

  const set = (key) => (value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    setFormError('');
  };

  const startOptions = useMemo(() => timeOptions('start', formatDigits), [formatDigits]);
  const endOptions = useMemo(() => timeOptions('end', formatDigits), [formatDigits]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    const found = validateDayOff(form, today);
    setErrors(found);
    setFormError('');
    const first = DAY_OFF_FIELDS.find((k) => found[k]);
    if (first) { document.getElementById(fieldId(first))?.focus?.(); return; }
    setBusy(true);
    const res = await onSubmit(dayOffVariables(form, clinicId));
    setBusy(false);
    if (res?.ok) { onClose?.(); return; }
    const key = res?.error?.extensions?.messageKey;
    const field = DAY_OFF_FIELD_OF_ERROR[key];
    if (field) {
      setErrors({ [field]: key });
      document.getElementById(fieldId(field))?.focus?.();
    } else {
      setFormError(userErrorMessage(res?.error, strings, 'settings.onlineBooking.dayOff.saveFailed'));
    }
  };

  const footer = (
    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', width: '100%' }}>
      <Button variant="outline" type="button" onClick={requestClose} disabled={busy} data-testid={`${testid}-cancel`}>
        {t('common.actions.cancel')}
      </Button>
      <Button type="submit" form={formId} disabled={busy} aria-busy={busy || undefined} data-testid={`${testid}-submit`}>
        {busy && <Spinner size={16} color="currentColor" />}
        {t('settings.onlineBooking.dayOff.submit')}
      </Button>
    </div>
  );

  const part = form.span === 'part';
  const err = (name) => (errors[name] ? t(errors[name]) : undefined);

  return (
    <TaskOverlay
      open={open}
      onClose={requestClose}
      title={t('settings.onlineBooking.dayOff.title')}
      description={t('settings.onlineBooking.dayOff.subtitle', { clinic: clinicName })}
      footer={footer}
      busy={busy}
      size="md"
      data-testid={testid}
    >
      <form id={formId} onSubmit={submit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(13rem, 100%), 1fr))', gap: '0.875rem' }}>
          {/* No htmlFor: FormField then hands the picker its message id and
              aria-invalid, so the error line is announced with the field. */}
          <FormField label={t('settings.onlineBooking.dayOff.from')} error={err('from')}>
            <JalaliDatePicker
              id={fieldId('from')}
              data-testid={fieldId('from')}
              value={form.from}
              onChange={(v) => set('from')(v || '')}
              disabled={busy}
            />
          </FormField>
          <FormField
            label={t('settings.onlineBooking.dayOff.to')}
            error={err('to')}
            helperText={errors.to ? undefined : t('settings.onlineBooking.dayOff.toHint')}
          >
            <JalaliDatePicker
              id={fieldId('to')}
              data-testid={fieldId('to')}
              value={form.to}
              onChange={(v) => set('to')(v || '')}
              disabled={busy}
            />
          </FormField>
        </div>

        <div>
          <div
            id={fieldId('span-label')}
            style={{ fontSize: '0.8125rem', fontWeight: 600, color: CT.textPrimary, marginBottom: '0.375rem' }}
          >
            {t('settings.onlineBooking.dayOff.span')}
          </div>
          <SegmentedControl
            semantics="filter"
            ariaLabel={t('settings.onlineBooking.dayOff.span')}
            aria-labelledby={fieldId('span-label')}
            value={form.span}
            onChange={set('span')}
            options={[
              { value: 'whole', label: t('settings.onlineBooking.dayOff.spanWhole'), disabled: busy },
              { value: 'part', label: t('settings.onlineBooking.dayOff.spanPart'), disabled: busy },
            ]}
            data-testid={fieldId('span')}
          />
        </div>

        {part && (
          <div data-testid={fieldId('hours')}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(10rem, 100%), 1fr))', gap: '0.875rem' }}>
              <Select
                id={fieldId('start')}
                data-testid={fieldId('start')}
                label={t('settings.onlineBooking.dayOff.start')}
                value={String(form.start)}
                onChange={(e) => set('start')(Number(e.target.value))}
                options={startOptions}
                placeholder=""
                disabled={busy}
              />
              <Select
                id={fieldId('end')}
                data-testid={fieldId('end')}
                label={t('settings.onlineBooking.dayOff.end')}
                value={String(form.end)}
                onChange={(e) => set('end')(Number(e.target.value))}
                options={endOptions}
                placeholder=""
                error={err('end')}
                disabled={busy}
              />
            </div>
            <p style={{ margin: '0.375rem 0 0', fontSize: '0.75rem', color: CT.textMuted }}>
              {t('settings.onlineBooking.dayOff.partHint')}
            </p>
          </div>
        )}

        <TextInput
          id={fieldId('reason')}
          data-testid={fieldId('reason')}
          label={t('settings.onlineBooking.dayOff.reason')}
          value={form.reason}
          onChange={(e) => set('reason')(e.target.value)}
          placeholder={t('settings.onlineBooking.dayOff.reasonPlaceholder')}
          maxLength={DAY_OFF_REASON_MAX}
          disabled={busy}
          dir="auto"
        />

        {formError && (
          <Alert variant="error" data-testid={`${testid}-error`}>{formError}</Alert>
        )}
      </form>
      {guard}
    </TaskOverlay>
  );
}
